/* Mummy & Munchkin — global behaviour.
   Drawers, cart API, header scroll, mega menu, countdown, tabs, predictive search.
   Vanilla, dependency free. */
(function () {
  'use strict';
  const MM = (window.MM = window.MM || {});
  const cfg = MM.config || { routes: {} };

  /* ---------- money ---------- */
  MM.formatMoney = function (cents, format) {
    const f = format || cfg.moneyFormat || 'Rs {{amount_no_decimals}}';
    const num = Number(cents || 0) / 100;
    const m = /\{\{\s*(\w+)\s*\}\}/.exec(f);
    const key = m ? m[1] : 'amount';
    let out;
    const fixed = (n, d) => n.toFixed(d);
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    switch (key) {
      case 'amount_no_decimals':
        out = group(fixed(num, 0), ',');
        break;
      case 'amount_with_comma_separator':
        out = group(fixed(num, 2).replace('.', ','), '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = group(fixed(num, 0), '.');
        break;
      case 'amount_with_apostrophe_separator':
        out = group(fixed(num, 2), "'");
        break;
      default:
        out = group(fixed(num, 2), ',');
    }
    return f.replace(/\{\{\s*\w+\s*\}\}/, out);
  };

  /* ---------- drawers (native <dialog>) ---------- */
  const drawers = {};
  MM.drawer = {
    get(id) {
      return drawers[id] || (drawers[id] = document.getElementById(id));
    },
    open(id, opener) {
      const d = this.get(id);
      if (!d || d.open) return;
      d.removeAttribute('data-closing');
      d._opener = opener || document.activeElement;
      d.showModal();
      document.documentElement.style.overflow = 'hidden';
      d.dispatchEvent(new CustomEvent('mm:drawer:open', { bubbles: true }));
      const focusEl = d.querySelector('[autofocus], input:not([type=hidden]), button, a');
      if (focusEl && !d.hasAttribute('data-no-autofocus')) setTimeout(() => focusEl.focus(), 260);
    },
    close(id) {
      const d = this.get(id);
      if (!d || !d.open) return;
      d.setAttribute('data-closing', '');
      const done = () => {
        d.removeAttribute('data-closing');
        d.close();
        document.documentElement.style.overflow = '';
        if (d._opener && d._opener.focus) d._opener.focus();
        d.dispatchEvent(new CustomEvent('mm:drawer:close', { bubbles: true }));
      };
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      reduce ? done() : setTimeout(done, 250);
    },
    toggle(id, opener) {
      const d = this.get(id);
      d && d.open ? this.close(id) : this.open(id, opener);
    },
  };

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-mm-open]');
    if (openBtn) {
      e.preventDefault();
      MM.drawer.open(openBtn.getAttribute('data-mm-open'), openBtn);
      return;
    }
    const closeBtn = e.target.closest('[data-mm-close]');
    if (closeBtn) {
      // links inside a drawer (e.g. 'View All Collections') close it AND navigate; buttons just close
      const isLink = closeBtn.tagName === 'A' && closeBtn.getAttribute('href') && closeBtn.getAttribute('href') !== '#';
      if (!isLink) e.preventDefault();
      const dlg = closeBtn.closest('dialog');
      if (dlg) MM.drawer.close(dlg.id);
      return;
    }
    // click on backdrop
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('mm-drawer')) {
      MM.drawer.close(e.target.id);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = Array.from(document.querySelectorAll('dialog[open].mm-drawer')).pop();
    if (!open) return;
    e.preventDefault();
    MM.drawer.close(open.id);
  });

  document.addEventListener(
    'cancel',
    (e) => {
      if (e.target instanceof HTMLDialogElement && e.target.classList.contains('mm-drawer')) {
        e.preventDefault();
        MM.drawer.close(e.target.id);
      }
    },
    true
  );

  /* ---------- cart ---------- */
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));

  function sectionsToRender() {
    const ids = new Set();
    document.querySelectorAll('[data-mm-cart-section]').forEach((el) => ids.add(el.getAttribute('data-mm-cart-section')));
    return Array.from(ids).join(',');
  }

  function applySections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach((id) => {
      const html = sections[id];
      const targets = document.querySelectorAll(`[data-mm-cart-section="${id}"]`);
      targets.forEach((target) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-mm-cart-section="${id}"]`);
        if (fresh) target.innerHTML = fresh.innerHTML;
      });
    });
  }

  function updateCounts(cart) {
    const n = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
    if (n === null) return;
    document.querySelectorAll('[data-mm-cart-count]').forEach((el) => {
      el.textContent = n;
      el.toggleAttribute('hidden', n === 0 && !el.hasAttribute('data-mm-cart-count-keep'));
    });
  }

  async function cartRequest(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: sectionsToRender() }, body)),
    });
    const data = await res.json();
    if (!res.ok) throw data;
    return data;
  }

  MM.cart = {
    async add(items, opts = {}) {
      const data = await cartRequest(cfg.routes.cartAdd + '.js', { items });
      const cart = await MM.cart.refresh();
      applySections(data.sections);
      emit('mm:cart:add', { items, cart });
      if (cfg.cartType === 'drawer' && !opts.silent) {
        const lastName = (data.items && data.items[0] && data.items[0].product_title) || '';
        const el = document.querySelector('[data-mm-last-added]');
        if (el) {
          el.textContent = lastName ? '· ' + lastName : '';
          el.parentElement && el.parentElement.removeAttribute('hidden');
        }
        if (document.getElementById('mm-cart-drawer')) MM.drawer.open('mm-cart-drawer');
      } else if (!opts.silent) {
        window.location.href = cfg.routes.cart;
      }
      return cart;
    },
    async change(line, quantity) {
      const data = await cartRequest(cfg.routes.cartChange + '.js', { line, quantity });
      applySections(data.sections);
      updateCounts(data);
      emit('mm:cart:change', { cart: data });
      return data;
    },
    async changeKey(id, quantity) {
      const data = await cartRequest(cfg.routes.cartChange + '.js', { id, quantity });
      applySections(data.sections);
      updateCounts(data);
      emit('mm:cart:change', { cart: data });
      return data;
    },
    async refresh() {
      const res = await fetch(cfg.routes.cart + '.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      updateCounts(cart);
      return cart;
    },
  };

  // Add-to-cart forms: <form data-mm-atc>
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-mm-atc]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type=submit]');
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const quantity = Number(fd.get('quantity') || 1);
    if (!id) return;
    const props = {};
    fd.forEach((v, k) => {
      const m = /^properties\[(.+)\]$/.exec(k);
      if (m && v) props[m[1]] = v;
    });
    const item = { id, quantity };
    if (Object.keys(props).length) item.properties = props;
    btn && btn.setAttribute('aria-busy', 'true');
    form.classList.add('mm-is-loading');
    try {
      await MM.cart.add([item]);
      form.dispatchEvent(new CustomEvent('mm:atc:success', { bubbles: true }));
    } catch (err) {
      const msg = form.querySelector('[data-mm-atc-error]');
      if (msg) {
        msg.textContent = (err && (err.description || err.message)) || 'Could not add to bag. Please try again.';
        msg.removeAttribute('hidden');
      }
    } finally {
      btn && btn.removeAttribute('aria-busy');
      form.classList.remove('mm-is-loading');
    }
  });

  // Quick Add / add-on buttons: <button data-mm-add="variantId">
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-mm-add]');
    if (!btn) return;
    e.preventDefault();
    const id = Number(btn.getAttribute('data-mm-add'));
    if (!id) return;
    const silent = btn.hasAttribute('data-mm-add-silent');
    btn.setAttribute('aria-busy', 'true');
    try {
      await MM.cart.add([{ id, quantity: 1 }], { silent });
      if (btn.isConnected) {
        btn.textContent = btn.getAttribute('data-mm-added-label') || 'Added ✓';
        btn.classList.add('mm-chip--active', 'is-added');
        btn.disabled = true;
      }
    } catch (err) {
      btn.textContent = 'Try Again';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  // Quantity steppers & remove inside cart: [data-mm-qty] [data-mm-line]
  document.addEventListener('click', async (e) => {
    const step = e.target.closest('[data-mm-qty]');
    const remove = e.target.closest('[data-mm-remove]');
    const row = (step || remove) && (step || remove).closest('[data-mm-line]');
    if (!row) return;
    e.preventDefault();
    const key = row.getAttribute('data-mm-line');
    const current = Number(row.getAttribute('data-mm-line-qty') || 1);
    let next = current;
    if (remove) next = 0;
    else next = step.getAttribute('data-mm-qty') === 'inc' ? current + 1 : Math.max(0, current - 1);
    row.classList.add('mm-is-loading');
    try {
      await MM.cart.changeKey(key, next);
    } catch (err) {
      row.classList.remove('mm-is-loading');
    }
  });

  // Manual quantity inputs in cart page: <input data-mm-qty-input>
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-mm-qty-input]');
    if (!input) return;
    const row = input.closest('[data-mm-line]');
    if (!row) return;
    row.classList.add('mm-is-loading');
    try {
      await MM.cart.changeKey(row.getAttribute('data-mm-line'), Math.max(0, Number(input.value) || 0));
    } catch (err) {
      row.classList.remove('mm-is-loading');
    }
  });

  /* ---------- header: hide on scroll down, show on scroll up ---------- */
  function initHeader() {
    const header = document.querySelector('[data-mm-header]');
    if (!header) return;
    const wrap = header.closest('#header-group') || header.closest('.shopify-section') || header;
    const setVar = () => {
      document.documentElement.style.setProperty('--mm-header-offset', header.offsetHeight + 'px');
      const group = header.closest('#header-group') || header.parentElement;
      if (group) document.documentElement.style.setProperty('--mm-header-group-h', group.offsetHeight + 'px');
    };
    setVar();
    new ResizeObserver(setVar).observe(header);
    if (header.getAttribute('data-mm-header') !== 'scroll-up') return;
    let last = window.scrollY;
    let ticking = false;
    const threshold = 8;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const y = window.scrollY;
          const diff = y - last;
          const anyOpen = document.querySelector('dialog[open].mm-drawer');
          if (!anyOpen) {
            if (y > 120 && diff > threshold) wrap.setAttribute('data-hidden', '');
            else if (diff < -threshold || y < 60) wrap.removeAttribute('data-hidden');
          }
          last = y;
          ticking = false;
        });
      },
      { passive: true }
    );
  }

  /* ---------- mega menu (desktop) ---------- */
  function initMegaMenu() {
    const nav = document.querySelector('[data-mm-nav]');
    if (!nav) return;
    const items = nav.querySelectorAll('[data-mm-nav-item]');
    let closeTimer = null;
    let openItem = null;
    const scrim = document.querySelector('[data-mm-mega-scrim]');
    const open = (item) => {
      clearTimeout(closeTimer);
      if (openItem && openItem !== item) close(openItem, true);
      openItem = item;
      item.setAttribute('data-open', '');
      item.querySelector('[data-mm-nav-link]')?.setAttribute('aria-expanded', 'true');
      scrim && scrim.removeAttribute('hidden');
    };
    const close = (item, immediate) => {
      const run = () => {
        item.removeAttribute('data-open');
        item.querySelector('[data-mm-nav-link]')?.setAttribute('aria-expanded', 'false');
        if (openItem === item) {
          openItem = null;
          scrim && scrim.setAttribute('hidden', '');
        }
      };
      immediate ? run() : (closeTimer = setTimeout(run, 150));
    };
    items.forEach((item) => {
      const panel = item.querySelector('[data-mm-mega]');
      if (!panel) return;
      item.addEventListener('mouseenter', () => open(item));
      item.addEventListener('mouseleave', () => close(item));
      const link = item.querySelector('[data-mm-nav-link]');
      link?.addEventListener('click', (e) => {
        // On touch screens the first tap opens the panel; the second follows the link.
        if (!matchMedia('(hover: hover)').matches && !item.hasAttribute('data-open')) {
          e.preventDefault();
          open(item);
        }
      });
      link?.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown' || e.key === ' ') {
          e.preventDefault();
          open(item);
          panel.querySelector('a')?.focus();
        }
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close(item, true);
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openItem) close(openItem, true);
    });
    scrim?.addEventListener('mouseenter', () => openItem && close(openItem, true));
  }

  /* ---------- mobile menu: level 2 panels ---------- */
  function initMobileMenu() {
    const menu = document.getElementById('mm-menu-drawer');
    if (!menu) return;
    const root = menu.querySelector('[data-mm-menu-root]');
    const subs = menu.querySelectorAll('[data-mm-menu-sub]');
    const show = (handle) => {
      subs.forEach((s) => {
        const on = s.getAttribute('data-mm-menu-sub') === handle;
        s.toggleAttribute('hidden', !on);
        if (on) {
          s.scrollTop = 0;
          s.querySelector('button, a')?.focus();
        }
      });
      root.toggleAttribute('hidden', !!handle);
      if (!handle) root.querySelector('button, a')?.focus();
    };
    menu.addEventListener('click', (e) => {
      const openSub = e.target.closest('[data-mm-menu-open]');
      if (openSub) {
        e.preventDefault();
        show(openSub.getAttribute('data-mm-menu-open'));
        return;
      }
      if (e.target.closest('[data-mm-menu-back]')) {
        e.preventDefault();
        show(null);
      }
    });
    menu.addEventListener('mm:drawer:close', () => setTimeout(() => show(null), 250));
  }

  /* ---------- predictive search ---------- */
  function initSearch() {
    document.querySelectorAll('[data-mm-search]').forEach((box) => {
      const input = box.querySelector('input[type=search]');
      const idle = box.querySelector('[data-mm-search-idle]');
      const results = box.querySelector('[data-mm-search-results]');
      const sectionId = box.getAttribute('data-mm-search');
      if (!input || !results) return;
      let timer = null;
      let controller = null;
      const render = async (q) => {
        if (!q) {
          results.innerHTML = '';
          results.setAttribute('hidden', '');
          idle && idle.removeAttribute('hidden');
          return;
        }
        controller && controller.abort();
        controller = new AbortController();
        const url =
          cfg.routes.predictiveSearch +
          '?q=' +
          encodeURIComponent(q) +
          '&resources[type]=product,collection&resources[limit]=4&resources[limit_scope]=each&section_id=' +
          encodeURIComponent(sectionId);
        try {
          const res = await fetch(url, { signal: controller.signal });
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('[data-mm-search-section]');
          results.innerHTML = inner ? inner.innerHTML : '';
          results.removeAttribute('hidden');
          idle && idle.setAttribute('hidden', '');
        } catch (err) {
          if (err.name !== 'AbortError') console.warn('[mm-search]', err);
        }
      };
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => render(input.value.trim()), 220);
      });
      box.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-mm-search-term]');
        if (!chip) return;
        e.preventDefault();
        input.value = chip.getAttribute('data-mm-search-term');
        input.focus();
        render(input.value.trim());
      });
    });
  }

  /* ---------- countdown ---------- */
  function initCountdowns() {
    const els = document.querySelectorAll('[data-mm-countdown]');
    if (!els.length) return;
    const z = (n) => String(n).padStart(2, '0');
    const tick = () => {
      els.forEach((el) => {
        const to = new Date(el.getAttribute('data-mm-countdown')).getTime();
        if (isNaN(to)) return;
        const ms = Math.max(0, to - Date.now());
        const d = Math.floor(ms / 864e5),
          h = Math.floor(ms / 36e5) % 24,
          m = Math.floor(ms / 6e4) % 60;
        el.querySelector('[data-d]').textContent = z(d);
        el.querySelector('[data-h]').textContent = z(h);
        el.querySelector('[data-m]').textContent = z(m);
        if (ms === 0) el.setAttribute('data-done', '');
      });
    };
    tick();
    setInterval(tick, 30000);
  }

  /* ---------- tabs ---------- */
  function initTabs() {
    document.querySelectorAll('[data-mm-tabs]').forEach((wrap) => {
      const tabs = wrap.querySelectorAll('[role=tab]');
      const panels = wrap.querySelectorAll('[role=tabpanel]');
      const select = (tab) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => p.toggleAttribute('hidden', p.id !== tab.getAttribute('aria-controls')));
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
            tabs[n].focus();
            select(tabs[n]);
          }
        });
      });
    });
  }

  /* ---------- wishlist (localStorage) ---------- */
  function initSave() {
    const KEY = 'mm-saved';
    const read = () => {
      try {
        return JSON.parse(localStorage.getItem(KEY) || '[]');
      } catch (e) {
        return [];
      }
    };
    const paint = () => {
      const saved = read();
      document.querySelectorAll('[data-mm-save]').forEach((b) => {
        b.setAttribute('aria-pressed', saved.includes(b.getAttribute('data-mm-save')) ? 'true' : 'false');
      });
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-mm-save]');
      if (!b) return;
      e.preventDefault();
      const id = b.getAttribute('data-mm-save');
      let saved = read();
      saved = saved.includes(id) ? saved.filter((x) => x !== id) : saved.concat(id);
      try {
        localStorage.setItem(KEY, JSON.stringify(saved));
      } catch (err) {
        /* private mode */
      }
      paint();
    });
    paint();
    document.addEventListener('mm:grid:update', paint);
  }

  /* ---------- generic toggles ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-mm-toggle]');
    if (!t) return;
    const target = document.getElementById(t.getAttribute('data-mm-toggle'));
    if (!target) return;
    const open = target.toggleAttribute('hidden') === false;
    t.setAttribute('aria-expanded', open ? 'true' : 'false');
    const alt = t.getAttribute('data-mm-toggle-label');
    if (alt) {
      const cur = t.textContent;
      t.textContent = alt;
      t.setAttribute('data-mm-toggle-label', cur);
    }
  });

  /* ---------- announcement bar: rotate messages ---------- */
  function initAnnouncement() {
    document.querySelectorAll('[data-mm-rotate]').forEach((bar) => {
      if (bar.dataset.mmRotateReady) return;
      bar.dataset.mmRotateReady = '1';
      const items = [...bar.querySelectorAll('.mm-announcement__item')];
      if (items.length < 2) return;
      const every = Math.max(2, parseFloat(bar.dataset.mmRotate) || 5) * 1000;
      let i = Math.max(0, items.findIndex((el) => el.classList.contains('is-active')));
      let timer = null;
      let paused = false;
      const show = (n) => {
        const prev = items[i];
        prev.classList.remove('is-active');
        prev.classList.add('is-leaving');
        setTimeout(() => prev.classList.remove('is-leaving'), 550);
        i = (n + items.length) % items.length;
        items[i].classList.add('is-active');
      };
      const stop = () => {
        if (timer) clearInterval(timer);
        timer = null;
      };
      const start = () => {
        stop();
        if (!paused && !document.hidden) timer = setInterval(() => show(i + 1), every);
      };
      const pause = () => { paused = true; stop(); };
      const resume = () => { paused = false; start(); };
      bar.addEventListener('mouseenter', pause);
      bar.addEventListener('mouseleave', resume);
      bar.addEventListener('focusin', pause);
      bar.addEventListener('focusout', resume);
      document.addEventListener('visibilitychange', start);
      // Theme editor: show the block being edited and hold it there.
      document.addEventListener('shopify:block:select', (e) => {
        const idx = items.indexOf(e.target.closest ? e.target.closest('.mm-announcement__item') : null);
        if (idx < 0) return;
        pause();
        show(idx);
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (bar.contains(e.target)) resume();
      });
      start();
    });
  }

  /* ---------- quick add drawer (product cards) ---------- */
  function initQuickAdd() {
    const dlg = document.getElementById('mm-quick-add');
    if (!dlg || dlg.dataset.mmQaReady) return;
    dlg.dataset.mmQaReady = '1';
    const $ = (sel) => dlg.querySelector(sel);
    const els = {
      loading: $('[data-mm-qa-loading]'), content: $('[data-mm-qa-content]'), img: $('[data-mm-qa-img]'),
      name: $('[data-mm-qa-name]'), price: $('[data-mm-qa-price]'), options: $('[data-mm-qa-options]'),
      error: $('[data-mm-qa-error]'), desc: $('[data-mm-qa-desc]'), links: dlg.querySelectorAll('[data-mm-qa-link]'),
      add: $('[data-mm-qa-add]'), addLabel: $('[data-mm-qa-add-label]'),
    };
    const cache = {};
    const money = (cents) => {
      if (MM.formatMoney) return MM.formatMoney(cents);
      const f = (cfg.moneyFormat || 'Rs.{{amount}}').replace(/<[^>]*>/g, '');
      const n = cents / 100;
      const out = { amount: n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), amount_no_decimals: Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') };
      return f.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => out[k] != null ? out[k] : out.amount);
    };
    let product = null;
    let selected = [];
    let variant = null;

    const findVariant = () => product.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    const availableFor = (idx, value) =>
      product.variants.some((v) => v.available && v.options[idx] === value && v.options.every((o, i) => i === idx || selected[i] == null || o === selected[i]));

    function paint() {
      variant = findVariant();
      const v = variant || product.variants.find((x) => x.available) || product.variants[0];
      const onSale = v && v.compare_at_price && v.compare_at_price > v.price;
      els.price.className = 'mm-price mm-qa__price' + (onSale ? ' mm-price--sale' : '');
      els.price.innerHTML = v ? `<span class="mm-price__now">${money(v.price)}</span>${onSale ? `<s class="mm-price__was">${money(v.compare_at_price)}</s>` : ''}` : '';
      if (variant && variant.featured_image && variant.featured_image.src) els.img.src = variant.featured_image.src;
      product.options.forEach((opt, i) => {
        const head = els.options.querySelector(`[data-mm-qa-opt="${i}"] [data-mm-qa-opt-value]`);
        if (head) head.textContent = selected[i] || `select ${opt.name.toLowerCase()}`;
        els.options.querySelectorAll(`[data-mm-qa-opt="${i}"] .mm-qa__box`).forEach((b) => {
          const val = b.getAttribute('data-mm-qa-value');
          b.setAttribute('aria-pressed', selected[i] === val ? 'true' : 'false');
          b.classList.toggle('is-soldout', !availableFor(i, val));
        });
      });
      const complete = selected.every((s) => s != null);
      els.add.disabled = !(complete && variant && variant.available);
      els.addLabel.textContent = !complete ? 'Select Options' : !variant || !variant.available ? 'Sold Out' : 'Add to Bag · ' + money(variant.price);
      els.error.hidden = true;
    }

    function render(p) {
      product = p;
      const only = p.options.length === 1 && p.options[0].values.length === 1 && /^(default title|title)$/i.test(p.options[0].values[0]);
      selected = p.options.map((o, i) => (only ? o.values[0] : o.values.length === 1 ? o.values[0] : null));
      els.name.textContent = p.title;
      els.img.src = (p.featured_image || (p.images && p.images[0]) || '');
      els.img.alt = p.title;
      els.links.forEach((a) => (a.href = p.url || `/products/${p.handle}`));
      const text = (p.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      els.desc.textContent = text.length > 180 ? text.slice(0, 177).replace(/\s\S*$/, '') + '…' : text;
      els.options.innerHTML = only
        ? ''
        : p.options
            .map(
              (opt, i) => `<div class="mm-qa__opt" data-mm-qa-opt="${i}" role="group" aria-label="${opt.name}">
          <div class="mm-qa__opt-head"><b>${opt.name}:</b> <span data-mm-qa-opt-value></span></div>
          <div class="mm-qa__values">${opt.values.map((v) => `<button type="button" class="mm-qa__box" data-mm-qa-value="${v.replace(/"/g, '&quot;')}" aria-pressed="false">${v}</button>`).join('')}</div>
        </div>`
            )
            .join('');
      els.loading.hidden = true;
      els.content.hidden = false;
      paint();
    }

    els.options.addEventListener('click', (e) => {
      const b = e.target.closest('.mm-qa__box');
      if (!b) return;
      const i = Number(b.closest('[data-mm-qa-opt]').getAttribute('data-mm-qa-opt'));
      selected[i] = selected[i] === b.getAttribute('data-mm-qa-value') && product.options[i].values.length > 1 ? null : b.getAttribute('data-mm-qa-value');
      paint();
    });

    els.add.addEventListener('click', async () => {
      if (!variant) {
        els.error.textContent = 'Please select your options first.';
        els.error.hidden = false;
        return;
      }
      els.add.setAttribute('aria-busy', 'true');
      try {
        await MM.cart.add([{ id: variant.id, quantity: 1 }], { silent: true });
        MM.drawer.close('mm-quick-add');
        setTimeout(() => {
          const el = document.querySelector('[data-mm-last-added]');
          if (el) { el.textContent = '· ' + product.title; el.parentElement && el.parentElement.removeAttribute('hidden'); }
          if (cfg.cartType === 'drawer' && document.getElementById('mm-cart-drawer')) MM.drawer.open('mm-cart-drawer');
          else window.location.href = cfg.routes.cart;
        }, 280);
      } catch (err) {
        els.error.textContent = (err && err.description) || 'Could not add to bag. Please try again.';
        els.error.hidden = false;
      } finally {
        els.add.removeAttribute('aria-busy');
      }
    });

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-mm-quick]');
      if (!btn) return;
      e.preventDefault();
      const handle = btn.getAttribute('data-mm-quick');
      els.loading.hidden = false;
      els.content.hidden = true;
      els.add.disabled = true;
      els.addLabel.textContent = 'Add to Bag';
      MM.drawer.open('mm-quick-add', btn);
      try {
        if (!cache[handle]) {
          const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(res.status);
          cache[handle] = await res.json();
        }
        render(cache[handle]);
      } catch (err) {
        window.location.href = btn.getAttribute('data-mm-quick-url') || `/products/${handle}`;
      }
    });
  }

  /* ---------- footer: WhatsApp sale alerts (number goes into the chat message) ---------- */
  function initWhatsAppJoin() {
    document.querySelectorAll('form[data-mm-wa-join]').forEach((form) => {
      if (form.dataset.mmWaReady) return;
      form.dataset.mmWaReady = '1';
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('input[type="tel"]');
        const number = (input?.value || '').replace(/[^\d+]/g, '');
        if (number.length < 10) {
          input?.focus();
          return;
        }
        const text = `${form.dataset.mmWaText || ''} ${number}`.trim();
        window.open(`${form.dataset.mmWaJoin}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
        form.reset();
      });
    });
  }

  /* ---------- boot ---------- */
  const boot = () => {
    initQuickAdd();
    initWhatsAppJoin();
    initAnnouncement();
    initHeader();
    initMegaMenu();
    initMobileMenu();
    initSearch();
    initCountdowns();
    initTabs();
    initSave();
    MM.cart.refresh().catch(() => {});
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();

  // Theme editor: re-init when sections reload
  document.addEventListener('shopify:section:load', () => {
    initWhatsAppJoin();
    initAnnouncement();
    initMegaMenu();
    initMobileMenu();
    initSearch();
    initCountdowns();
    initTabs();
  });
})();
